import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Card, DatePicker, Input, Select, Space, Table, Tag, Typography, message } from 'antd'
import dayjs from 'dayjs'
import { getSubscriptions } from '../../services/subscriptions'
import { formatTimestamp } from '../../utils/format'
import { formatAmount } from '../../utils/orderDisplay'
import type { MerchantSubscriptionListItem, Pagination, SubscriptionStatus } from '../../types'
import {
  billingReasonText,
  canCancelSubscription,
  formatCard,
  formatSubscriptionTerm,
  invoiceStatusMeta,
  subscriptionStatusMeta
} from './subscriptionLabels'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

const initialPagination: Pagination = { page: 1, size: 20, total: 0, totalPages: 0 }

const statusOptions: Array<{ value: SubscriptionStatus; label: string }> = [
  { value: 'PENDING', label: '待開通' },
  { value: 'ACTIVE', label: '生效中' },
  { value: 'PAST_DUE', label: '逾期' },
  { value: 'SUSPENDED', label: '已暫停' },
  { value: 'CANCEL_AT_PERIOD_END', label: '週期末取消' },
  { value: 'CANCELLED', label: '已取消' },
  { value: 'TERMINATED', label: '已終止' }
]

export default function SubscriptionList() {
  const navigate = useNavigate()
  const [subscriptions, setSubscriptions] = useState<MerchantSubscriptionListItem[]>([])
  const [pagination, setPagination] = useState<Pagination>(initialPagination)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [status, setStatus] = useState<SubscriptionStatus | undefined>()
  const [subscriptionIdInput, setSubscriptionIdInput] = useState('')
  const [subscriptionId, setSubscriptionId] = useState<string | undefined>()
  const [emailInput, setEmailInput] = useState('')
  const [customerEmail, setCustomerEmail] = useState<string | undefined>()
  const [planIdInput, setPlanIdInput] = useState('')
  const [planId, setPlanId] = useState<string | undefined>()
  const [createdRange, setCreatedRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null])
  const [nextBillingRange, setNextBillingRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null])

  const fetchSubscriptions = (page: number = 1, size: number = pagination.size || initialPagination.size) => {
    setLoading(true)
    const params: Parameters<typeof getSubscriptions>[0] = {
      page,
      size,
      status,
      subscriptionId,
      customerEmail,
      planId
    }
    if (createdRange[0] && createdRange[1]) {
      params.createdStartAt = createdRange[0].startOf('day').valueOf()
      params.createdEndAt = createdRange[1].endOf('day').valueOf()
    }
    if (nextBillingRange[0] && nextBillingRange[1]) {
      params.nextBillingStartAt = nextBillingRange[0].startOf('day').valueOf()
      params.nextBillingEndAt = nextBillingRange[1].endOf('day').valueOf()
    }
    getSubscriptions(params)
      .then((res) => {
        setSubscriptions(res.data.items)
        setPagination(res.data.pagination)
        setLoadError(null)
      })
      .catch((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : '載入訂閱列表失敗'
        message.error(errorMessage)
        setLoadError(errorMessage)
        setSubscriptions([])
        setPagination(initialPagination)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchSubscriptions(1)
  }, [status, subscriptionId, customerEmail, planId, createdRange, nextBillingRange])

  const handleReset = () => {
    setStatus(undefined)
    setSubscriptionIdInput('')
    setSubscriptionId(undefined)
    setEmailInput('')
    setCustomerEmail(undefined)
    setPlanIdInput('')
    setPlanId(undefined)
    setCreatedRange([null, null])
    setNextBillingRange([null, null])
  }

  const columns = [
    {
      title: '訂閱 ID',
      dataIndex: 'id',
      key: 'id',
      width: 220
    },
    {
      title: '客戶',
      key: 'customer',
      width: 220,
      render: (_: unknown, record: MerchantSubscriptionListItem) => (
        <Space direction="vertical" size={0}>
          <Text>{record.customerEmail || '-'}</Text>
          {record.customerName && <Text type="secondary">{record.customerName}</Text>}
        </Space>
      )
    },
    {
      title: '計劃',
      key: 'plan',
      width: 220,
      render: (_: unknown, record: MerchantSubscriptionListItem) => (
        <Space direction="vertical" size={0}>
          <Text>{record.planName || record.planId}</Text>
          <Text type="secondary">{formatAmount(record.planAmount)} {record.planCurrency} / {formatSubscriptionTerm(record.billingInterval, record.billingIntervalCount, record.totalBillingCycles)}</Text>
        </Space>
      )
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (value: SubscriptionStatus) => {
        const meta = subscriptionStatusMeta[value]
        return <Tag color={meta.color}>{meta.label}</Tag>
      }
    },
    {
      title: '支付方式',
      key: 'paymentMethod',
      width: 160,
      render: (_: unknown, record: MerchantSubscriptionListItem) => formatCard(record.cardBrand, record.cardLast4)
    },
    {
      title: '最近扣款',
      key: 'latestInvoice',
      width: 220,
      render: (_: unknown, record: MerchantSubscriptionListItem) => {
        const invoice = record.latestInvoice
        if (!invoice) return '-'
        const meta = invoiceStatusMeta[invoice.status]
        return (
          <Space direction="vertical" size={0}>
            <span>{formatAmount(invoice.amount)} {invoice.currency} <Tag color={meta.color}>{meta.label}</Tag></span>
            <Text type="secondary">{billingReasonText[invoice.billingReason]} · {formatTimestamp(invoice.paidAt || invoice.updatedAt)}</Text>
          </Space>
        )
      }
    },
    {
      title: '當前週期',
      key: 'period',
      width: 260,
      render: (_: unknown, record: MerchantSubscriptionListItem) => (
        <span>{formatTimestamp(record.currentPeriodStartAt)} ～ {formatTimestamp(record.currentPeriodEndAt)}</span>
      )
    },
    {
      title: '下次扣款 / 取消',
      key: 'nextBillingAt',
      width: 220,
      render: (_: unknown, record: MerchantSubscriptionListItem) => (
        <Space direction="vertical" size={0}>
          <Text>{formatTimestamp(record.nextBillingAt)}</Text>
          {record.cancelAtPeriodEnd && <Tag color="blue">週期末取消：{formatTimestamp(record.cancelAt)}</Tag>}
          {record.cancelledAt && <Text type="secondary">已取消：{formatTimestamp(record.cancelledAt)}</Text>}
        </Space>
      )
    },
    {
      title: '創建時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (value: number) => formatTimestamp(value)
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_: unknown, record: MerchantSubscriptionListItem) => (
        <Space size="small">
          <Button type="link" size="small" onClick={(event) => {
            event.stopPropagation()
            navigate(`/dashboard/subscriptions/${record.id}`)
          }}>
            詳情
          </Button>
          {canCancelSubscription(record.status) && <Tag color="orange">可取消</Tag>}
        </Space>
      )
    }
  ]

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>訂閱管理</Title>
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input.Search
            placeholder="訂閱 ID"
            allowClear
            enterButton="查詢"
            style={{ width: 260 }}
            value={subscriptionIdInput}
            onChange={(event) => setSubscriptionIdInput(event.target.value)}
            onSearch={(value) => setSubscriptionId(value.trim() || undefined)}
          />
          <Input.Search
            placeholder="客戶郵箱"
            allowClear
            enterButton="篩選"
            style={{ width: 240 }}
            value={emailInput}
            onChange={(event) => setEmailInput(event.target.value)}
            onSearch={(value) => setCustomerEmail(value.trim() || undefined)}
          />
          <Input.Search
            placeholder="計劃 ID"
            allowClear
            enterButton="篩選"
            style={{ width: 240 }}
            value={planIdInput}
            onChange={(event) => setPlanIdInput(event.target.value)}
            onSearch={(value) => setPlanId(value.trim() || undefined)}
          />
          <Select
            placeholder="訂閱狀態"
            allowClear
            style={{ width: 170 }}
            value={status}
            onChange={setStatus}
            options={statusOptions}
          />
          <RangePicker
            value={createdRange}
            onChange={(dates) => setCreatedRange(dates || [null, null])}
            placeholder={['創建開始', '創建結束']}
          />
          <RangePicker
            value={nextBillingRange}
            onChange={(dates) => setNextBillingRange(dates || [null, null])}
            placeholder={['下次扣款開始', '下次扣款結束']}
          />
          <Button onClick={handleReset}>重置</Button>
        </Space>
        {loadError && (
          <Alert
            type="error"
            showIcon
            message="載入訂閱列表失敗"
            description={loadError}
            action={<Button size="small" onClick={() => fetchSubscriptions(pagination.page || 1, pagination.size || initialPagination.size)}>重新載入</Button>}
            style={{ marginBottom: 16 }}
          />
        )}
        <Table
          className="subscription-list-table"
          columns={columns}
          dataSource={subscriptions}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({
            onClick: () => navigate(`/dashboard/subscriptions/${record.id}`),
            style: { cursor: 'pointer' }
          })}
          pagination={{
            current: pagination.page,
            pageSize: pagination.size,
            total: pagination.total,
            onChange: (page, size) => fetchSubscriptions(page, size)
          }}
        />
      </Card>
    </div>
  )
}
